import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { Dot } from "lucide-react";

import { cn } from "@/lib/utils";

const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, React.ComponentPropsWithoutRef<typeof OTPInput>>(
  ({ className, containerClassName, ...props }, ref) => (
    <OTPInput
      ref={ref}
      // overflow-x-clip: input-otp's invisible real <input> is 40px wider than the slots (visually
      // clip-path'ed, but still scrollable overflow). When it takes focus inside a scrolling
      // ancestor — a Dialog with overflow-y-auto — the browser scrolls that ancestor sideways to
      // reveal it, dragging the whole dialog body ~16px left (desktop „Zmień numer”, 23.10).
      // `clip` (not `hidden`) is the one overflow value nothing can scroll, by script or focus.
      containerClassName={cn("flex items-center gap-2 overflow-x-clip has-[:disabled]:opacity-50", containerClassName)}
      // text-base (16px) is REQUIRED on the real input: iOS Safari auto-zooms
      // the whole page when a focused input's font-size is below 16px, and the
      // zoom persists across navigation.
      className={cn("text-base disabled:cursor-not-allowed", className)}
      {...props}
    />
  ),
);
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("flex items-center", className)} {...props} />,
);
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div"> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      // Stable styling hook: the visible OTP "boxes" are DIVs, not inputs, so
      // rules that target form controls (e.g. the funnel's desktop scale-up)
      // miss them entirely — which is exactly what happened.
      data-otp-slot=""
      className={cn(
        "relative flex h-10 w-10 items-center justify-center border-y border-r border-input text-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md",
        isActive && "z-10 ring-2 ring-ring ring-offset-background",
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink h-4 w-px bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>(
  ({ ...props }, ref) => (
    <div ref={ref} role="separator" {...props}>
      <Dot />
    </div>
  ),
);
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
